import {Box, Flex, FormLabel, Text} from '@chakra-ui/react';
import { currencyFormat } from 'components/Number';
import ChartTooltip from 'components/charts/ChartTooltip';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import SelectInput from "../../../../../components/inputs/SelectInput";
import {generateSelectOptions} from "../../../../../helpers/inputHelper";

const CHART_TYPE = {
  BAR: 'bar',
  AREA: 'area',
};

const CHART_TYPE_META = {
  [CHART_TYPE.BAR]: {
    label: 'Bar',
  },
  [CHART_TYPE.AREA]: {
    label: 'Area',
  },
};

const SpendBarChart2 = ({ data }) => {
  const [chartData, setChartData] = useState();
  const [chartType, setChartType] = useState(CHART_TYPE.BAR);

  useEffect(() => {
    if (data) {
      const _chartData = getChartData(chartType);
      setChartData(_chartData);
    }
  }, [data, chartType]);

  const getCategoryLabel = (mDate, index) => {
    if (index === 0 || mDate.month() === 0) {
      return [mDate.format('MMM'), mDate.format('YYYY')];
    }

    return mDate.format('MMM');
  };

  const getChartOptions = ({ dates, xAxisCategories, maxTotal }) => {
    return {
      chart: {
        fontFamily: 'DM Sans',
        stacked: true,
        animations:{
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const total = series.reduce((sum, s) => sum + (s[dataPointIndex] || 0), 0);
          const title = dataPointIndex >=w.config.dates.length?"---":w.config.dates[dataPointIndex].format('MMM YYYY');

          let tooltipItems = [];

          for (let i = 0; i < series.length; i++) {
            const seriesName = w.config.series[i].name;
            const seriesValue = series[i][dataPointIndex] || 0;
            if (seriesValue !== 0) {
              tooltipItems.push({
                label: seriesName,
                color: w.config.series[i].color,
                value: currencyFormat(seriesValue),
              });
            }
          }

          tooltipItems.push(
              {
                html: "---",
              },
              { label: 'Total', value: currencyFormat(total) },
          )

          return renderToString(<ChartTooltip title={title} items={tooltipItems} />);
        },
      } ,
      xaxis: {
        categories: xAxisCategories,
      },
      yaxis: {
        min: 0,
        max: () => {
          return maxTotal;
        },
        labels: {
          formatter: (value) => {
            if (value === null) {
              return null;
            }
            return currencyFormat(value);
          },
        },
      },
      dates,
    };
  };

  const getChartData = (chartType) => {
    const _data = data.map((row) => {
      return {
        date: moment({
          year: row.date.year,
          month: row.date.month - 1,
          day: row.date.day,
        }),
        actualMarketingSpend: row.actual_marketing_spend || 0,
        predictedMarketingSpend: row.predicted_marketing_spend || 0,
        totalMarketingSpend: (row.actual_marketing_spend || 0) + (row.predicted_marketing_spend || 0),
        marketing_per_source: row.marketing_per_source,
      };
    });

    _data.sort((a, b) => a.date - b.date);

    let actualMarketingSpendData = [];
    let predictedMarketingSpendData = [];
    let totalMarketingSpendData = [];
    let xAxisCategories = [];
    let dates = [];
    let maxTotalValue = 0;

    let spendBySource = {}
    let spendBySourceKeys = []

    _data.forEach((row, index) => {
      if (row.marketing_per_source) {
          row.marketing_per_source.forEach((mps) => {
              if (!spendBySourceKeys.includes(mps.Source)) {
                spendBySourceKeys.push(mps.Source)
                spendBySource[mps.Source] = Array(_data.length).fill(0);
              }
          });
      }
    });

    _data.forEach((row, index) => {
      actualMarketingSpendData.push(row.actualMarketingSpend);
      predictedMarketingSpendData.push(row.predictedMarketingSpend);
      totalMarketingSpendData.push(row.totalMarketingSpend);
      xAxisCategories.push(getCategoryLabel(row.date, index));
      dates.push(row.date);
      if (row.totalMarketingSpend > maxTotalValue) {
        maxTotalValue = row.totalMarketingSpend;
      }
      if (row.marketing_per_source) {
        row.marketing_per_source.forEach((mps) => {
          spendBySource[mps.Source][index] = mps.spend || 0;
        });
      }
    });

    const colorPalette = [
      "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
      "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
      "#393b79", "#637939", "#8c6d31", "#843c39", "#7b4173",
      "#3182bd", "#e6550d", "#31a354", "#756bb1", "#636363",
      "#fdae6b", "#9ecae1", "#74c476", "#fd8d3c", "#c994c7",
      "#6baed6", "#9e9ac8", "#ff9896", "#98df8a", "#ffbb78",
      "#aec7e8", "#c5b0d5", "#c49c94", "#f7b6d2", "#dbdb8d",
      "#17becf", "#8dd3c7", "#ffffb3", "#bebada", "#fb8072",
      "#80b1d3", "#fdb462", "#b3de69", "#fccde5", "#d9d9d9",
      "#bc80bd", "#ccebc5", "#ffed6f", "#1b9e77", "#d95f02"
    ];

    let colorIndex = 0;
    const series = [];

    spendBySourceKeys.forEach((key ) => {
      series.push({
        name: key,
        data: spendBySource[key],
        type: chartType,
        color: colorPalette[colorIndex % colorPalette.length],
        lineColor: colorPalette[colorIndex % colorPalette.length],
        stroke: {
          show: true,
          curve: 'straight',
        }
      });
      colorIndex++
    });

    const options1 = getChartOptions({
      dates,
      xAxisCategories,
      maxTotal: maxTotalValue,
    });

    return {
      series: series,
      options: options1,
      chartType: chartType,
    };
  };

  if (!chartData) {
    return null;
  }

  return (
    <>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        <Text fontWeight={800} mb={10}>
          Marketing Monthly Spend By Source
        </Text>
        <Flex align={'center'} gap={1}>
          <FormLabel m={0}>Chart type: </FormLabel>
          <SelectInput
              options={generateSelectOptions({ metaObj: CHART_TYPE_META })}
              value={chartType}
              onChange={setChartType}
              w={200}
          />
        </Flex>
      </Flex>
      <Box minH={300}>
        <Chart
          type={chartData.chartType}
          series={chartData.series}
          options={chartData.options}
          width={'100%'}
          height={'100%'}
        />
      </Box>
    </>
  );
};

SpendBarChart2.propTypes = {
  data: PropTypes.array,
};

export default SpendBarChart2;
